import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { I18nProvider } from './contexts/I18nContext';
import { Toaster } from './components/ui/sonner';

// Layouts
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import AppSidebar from './components/layout/AppSidebar';
import AdminSidebar from './components/layout/AdminSidebar';

// Public Pages
import HomePage from './pages/public/HomePage';
import LoginPage from './pages/public/LoginPage';
import RegisterPage from './pages/public/RegisterPage';
import AuthCallback from './pages/public/AuthCallback';
import AboutPage from './pages/public/AboutPage';
import HowItWorksPage from './pages/public/HowItWorksPage';
import ContactPage from './pages/public/ContactPage';
import SubmitPropertyPage from './pages/public/SubmitPropertyPage';
import OpportunitiesPage from './pages/public/OpportunitiesPage';
import OpportunityDetailPage from './pages/public/OpportunityDetailPage';
import ServiciosPage from './pages/public/ServiciosPage';
import ProyectosPage from './pages/public/ProyectosPage';
import EquipoPage from './pages/public/EquipoPage';
import { LegalTermsPage, LegalPrivacyPage, LegalRiskDisclosuresPage } from './pages/public/LegalPages';

// App Pages
import InvestorDashboard from './pages/app/InvestorDashboard';
import PropertyContributorDashboard from './pages/app/PropertyContributorDashboard';
import ProfilePage from './pages/app/ProfilePage';
import InvestmentsPage from './pages/app/InvestmentsPage';
import CommitmentPage from './pages/app/CommitmentPage';
import PropertiesPage from './pages/app/PropertiesPage';
import PropertyFormPage from './pages/app/PropertyFormPage';
import AppOpportunitiesPage from './pages/app/AppOpportunitiesPage';
import AppOpportunityDetailPage from './pages/app/AppOpportunityDetailPage';
import DocumentsPage from './pages/app/DocumentsPage';
import MySignaturesPage from './pages/app/MySignaturesPage';
import SignatureRequestPage from './pages/app/SignatureRequestPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminUserDetailPage from './pages/admin/AdminUserDetailPage';
import AdminOpportunitiesPage from './pages/admin/AdminOpportunitiesPage';
import AdminOpportunityFormPage from './pages/admin/AdminOpportunityFormPage';
import AdminPropertiesPage from './pages/admin/AdminPropertiesPage';
import AdminPropertyDetailPage from './pages/admin/AdminPropertyDetailPage';
import AdminCommitmentsPage from './pages/admin/AdminCommitmentsPage';
import AdminAuditLogPage from './pages/admin/AdminAuditLogPage';
import AdminReportsPage from './pages/admin/AdminReportsPage';
import AdminSignaturesPage from './pages/admin/AdminSignaturesPage';
import AdminDocumentsPage from './pages/admin/AdminDocumentsPage';
import AdminTemplatesPage from './pages/admin/AdminTemplatesPage';
import AdminContentPage from './pages/admin/AdminContentPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';

import { Loader2 } from 'lucide-react';

// Public Layout Component
const PublicLayout = ({ children }) => (
  <>
    <Navbar />
    <main>{children}</main>
    <Footer />
  </>
);

// App Layout Component (for logged-in users)
const AppLayout = ({ children }) => {
  const { isAdmin } = useAuth();
  
  return (
    <div className="min-h-screen bg-background">
      <AppSidebar />
      <main className="lg:ml-64 min-h-screen pt-14 lg:pt-0">
        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

// Admin Layout Component
const AdminLayout = ({ children }) => (
  <div className="min-h-screen bg-background">
    <AdminSidebar />
    <main className="lg:ml-64 min-h-screen pt-14 lg:pt-0">
      <div className="p-4 lg:p-8">
        {children}
      </div>
    </main>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return children;
};

// Dashboard Router (routes to appropriate dashboard based on role)
const DashboardRouter = () => {
  const { user, isAdmin, isPropertyContributor } = useAuth();
  const navigate = useNavigate();
  
  // Admins should go to admin dashboard, not user dashboard
  useEffect(() => {
    if (isAdmin) {
      navigate('/admin', { replace: true });
    }
  }, [isAdmin, navigate]);
  
  // Show loading while redirecting admin
  if (isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Redirigiendo al panel de administración...</p>
        </div>
      </div>
    );
  }
  
  if (isPropertyContributor) {
    return <PropertyContributorDashboard />;
  }
  
  return <InvestorDashboard />;
};

// Placeholder component for pages not yet implemented
const PlaceholderPage = ({ title }) => (
  <div className="flex items-center justify-center h-64">
    <div className="text-center">
      <h1 className="text-2xl font-medium mb-2">{title}</h1>
      <p className="text-muted-foreground">Esta página está en desarrollo</p>
    </div>
  </div>
);

// Main App Router
const AppRouter = () => {
  const location = useLocation();

  // Check for OAuth callback session_id in hash
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
      <Route path="/nosotros" element={<PublicLayout><AboutPage /></PublicLayout>} />
      <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
      <Route path="/servicios" element={<PublicLayout><ServiciosPage /></PublicLayout>} />
      <Route path="/proyectos" element={<PublicLayout><ProyectosPage /></PublicLayout>} />
      <Route path="/equipo" element={<PublicLayout><EquipoPage /></PublicLayout>} />
      <Route path="/como-funciona" element={<PublicLayout><HowItWorksPage /></PublicLayout>} />
      <Route path="/how-it-works" element={<PublicLayout><HowItWorksPage /></PublicLayout>} />
      <Route path="/oportunidades" element={<PublicLayout><OpportunitiesPage /></PublicLayout>} />
      <Route path="/opportunities" element={<PublicLayout><OpportunitiesPage /></PublicLayout>} />
      <Route path="/oportunidades/:slug" element={<PublicLayout><OpportunityDetailPage /></PublicLayout>} />
      <Route path="/opportunities/:slug" element={<PublicLayout><OpportunityDetailPage /></PublicLayout>} />
      <Route path="/aportar-inmueble" element={<PublicLayout><SubmitPropertyPage /></PublicLayout>} />
      <Route path="/submit-property" element={<PublicLayout><SubmitPropertyPage /></PublicLayout>} />
      <Route path="/contacto" element={<PublicLayout><ContactPage /></PublicLayout>} />
      <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />
      <Route path="/legal/terms" element={<PublicLayout><LegalTermsPage /></PublicLayout>} />
      <Route path="/legal/privacy" element={<PublicLayout><LegalPrivacyPage /></PublicLayout>} />
      <Route path="/legal/risk-disclosures" element={<PublicLayout><LegalRiskDisclosuresPage /></PublicLayout>} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      
      {/* App Routes (Protected) */}
      <Route path="/app/dashboard" element={
        <ProtectedRoute>
          <AppLayout><DashboardRouter /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/app/profile" element={
        <ProtectedRoute>
          <AppLayout><ProfilePage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/app/opportunities" element={
        <ProtectedRoute>
          <AppLayout><AppOpportunitiesPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/app/opportunities/:slug" element={
        <ProtectedRoute>
          <AppLayout><AppOpportunityDetailPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/app/opportunities/:slug/invest" element={
        <ProtectedRoute>
          <AppLayout><CommitmentPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/app/investments" element={
        <ProtectedRoute>
          <AppLayout><InvestmentsPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/app/properties" element={
        <ProtectedRoute>
          <AppLayout><PropertiesPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/app/properties/new" element={
        <ProtectedRoute>
          <AppLayout><PropertyFormPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/app/properties/:id" element={
        <ProtectedRoute>
          <AppLayout><PropertyFormPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/app/documents" element={
        <ProtectedRoute>
          <AppLayout><DocumentsPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/app/signatures" element={
        <ProtectedRoute>
          <AppLayout><MySignaturesPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/app/signatures/:requestId" element={
        <ProtectedRoute>
          <AppLayout><SignatureRequestPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/app/messages" element={
        <ProtectedRoute>
          <AppLayout><PlaceholderPage title="Mensajes" /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/app/settings" element={
        <ProtectedRoute>
          <AppLayout><PlaceholderPage title="Configuración" /></AppLayout>
        </ProtectedRoute>
      } />
      
      {/* Admin Routes (Protected) */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['super_admin', 'fund_director', 'analyst', 'compliance']}>
          <AdminLayout><AdminDashboard /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute allowedRoles={['super_admin', 'fund_director', 'compliance']}>
          <AdminLayout><AdminUsersPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/users/:userId" element={
        <ProtectedRoute allowedRoles={['super_admin', 'fund_director', 'compliance']}>
          <AdminLayout><AdminUserDetailPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/opportunities" element={
        <ProtectedRoute allowedRoles={['super_admin', 'fund_director', 'analyst']}>
          <AdminLayout><AdminOpportunitiesPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/opportunities/new" element={
        <ProtectedRoute allowedRoles={['super_admin', 'fund_director']}>
          <AdminLayout><AdminOpportunityFormPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/opportunities/:opportunityId/edit" element={
        <ProtectedRoute allowedRoles={['super_admin', 'fund_director', 'analyst']}>
          <AdminLayout><AdminOpportunityFormPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/properties" element={
        <ProtectedRoute allowedRoles={['super_admin', 'fund_director', 'analyst']}>
          <AdminLayout><AdminPropertiesPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/properties/:propertyId" element={
        <ProtectedRoute allowedRoles={['super_admin', 'fund_director', 'analyst']}>
          <AdminLayout><AdminPropertyDetailPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/commitments" element={
        <ProtectedRoute allowedRoles={['super_admin', 'fund_director', 'analyst']}>
          <AdminLayout><AdminCommitmentsPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/compliance" element={
        <ProtectedRoute allowedRoles={['super_admin', 'fund_director', 'compliance']}>
          <AdminLayout><AdminUsersPage filterStatus="in_review" /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/documents" element={
        <ProtectedRoute allowedRoles={['super_admin', 'fund_director', 'analyst', 'compliance']}>
          <AdminLayout><AdminDocumentsPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/templates" element={
        <ProtectedRoute allowedRoles={['super_admin', 'fund_director']}>
          <AdminLayout><AdminTemplatesPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/content" element={
        <ProtectedRoute allowedRoles={['super_admin', 'fund_director']}>
          <AdminLayout><AdminContentPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/reports" element={
        <ProtectedRoute allowedRoles={['super_admin', 'fund_director', 'analyst']}>
          <AdminLayout><AdminReportsPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/signatures" element={
        <ProtectedRoute allowedRoles={['super_admin', 'fund_director', 'analyst', 'compliance']}>
          <AdminLayout><AdminSignaturesPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/audit-log" element={
        <ProtectedRoute allowedRoles={['super_admin', 'compliance']}>
          <AdminLayout><AdminAuditLogPage /></AdminLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <AdminLayout><AdminSettingsPage /></AdminLayout>
        </ProtectedRoute>
      } />
      
      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
          <AppRouter />
          <Toaster />
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

export default App;
